import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ButtonModule, CardModule, FormFloatingDirective, FormLabelDirective, FormModule, InputGroupComponent } from '@coreui/angular';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { iPlan, iPlanItems } from '../.././deliverables/progress-plan.model';
import { ToastService, IPolymorphicControlMeta, PolymorphicControlService, PolyMorphicControlTypes, PolymorphicControlComponent, IListView, ListerComponent } from '@optima-stride-ai/optima-components';
import { UnitCategory, Units, Unit, FieldTypes } from '@optima-stride-ai/core';
import { IconModule } from '@coreui/icons-angular';
import { UtilService } from '../../utils/util.service';
import { getPlanListView } from './plan-list-view';
import { launchPlanItemEditDialog, launchPlanItemDeleteDialog } from '../../modal/modal';
import { OptimaDefaultModalService } from '../../modal/modal.service';

export enum cadenceOptions {
  Daily = 1,
  Weekly,
  Monthly,
  Quarterly,
  Yearly,
  Fortnightly,
  HalfYearly,
}

@Component({
  selector: 'app-plan-form',
  imports: [
    CommonModule,
    FormModule,
    ReactiveFormsModule,
    InputGroupComponent,
    FormFloatingDirective,
    FormLabelDirective,
    IconModule,
    CardModule,
    ButtonModule,
    PolymorphicControlComponent,
    ListerComponent
  ],
  templateUrl: './plan-form.component.html',
  styleUrl: './plan-form.component.sass'
})
export class PlanFormComponent implements OnChanges {
  @Input() startValue: number = 0;
  @Input() endValue: number = 0;
  @Input() startDate: string = '';
  @Input() endDate: string = '';
  @Input() plan?: iPlan;
  @Input() isEditing: boolean = false;
  @Input() unit?: Unit;
  @Output() planGenerated = new EventEmitter<iPlan>(); 
  private toastService: ToastService = inject(ToastService);
  private readonly polymorphicControlService = inject(PolymorphicControlService);
  private readonly utilService = inject(UtilService);
  private readonly optimaDefaultModalService = inject(OptimaDefaultModalService);
  
  generatedPlan!: iPlan;
  planForm: iPlan = {
    cadence: 86400, // Default to Daily (in seconds)
    tolerance: 5,
    planItems: [],
    deliverableId: ''
  };
  cadenceOptions = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Half-yearly'];
  listView: IListView<iPlan> = {
    title: 'Generated Plan',
    colHeaders: [
      { key: "#", label:"#", cssClass:"label"},
      { key: "date", label:"Date", cssClass:"date-label"},
      { key: "expectedValue", label:"Expected Value", cssClass:"expected-value-label"},
      { key: "unit", label:"Unit", cssClass:"unit-label"}
    ],
    items: [],
    isStriped: true,
  };
  selectedPlanItemId: string = '';
  // Polymorphic controls
  polyCadenceControl!: IPolymorphicControlMeta;
  polyToleranceControl!: IPolymorphicControlMeta;
  isFormValid: boolean = true;

  constructor() {
  }

 ngOnChanges(changes: SimpleChanges): void {
    // Sync plan data from parent when in edit mode (triggered by plan or unit changes)
    if ((changes['plan'] || changes['unit']) && this.plan && this.isEditing) {
      // console.log("Plan or unit input changed, syncing form data", this.unit);
      this.planForm = { ...this.plan };
      this.generatedPlan = { ...this.plan };
      
      // Update list view with existing plan
      if (this.plan.planItems && this.plan.planItems.length > 0) {
        this.listView = this.buildPlanListView(this.plan);
      }
      // console.log("Plan input changed, syncing form data", this.plan);
      this.buildPolymorphicControls();
    }
    
    // Only rebuild controls if this is the first change or cadence/tolerance changed
    if (!this.polyCadenceControl || !this.polyToleranceControl) {
      this.buildPolymorphicControls();
    }

    // Auto-generate when date or value inputs change, but only once all relevant inputs are actually populated.
    // Restricted to create mode: in edit mode there's an existing saved plan to protect, and the
    // parent's async data loading can race with these inputs - regeneration there only happens
    // through explicit user actions (Regenerate Plan button, cadence change, parent-driven calls).
    if (!this.isEditing) {
      const hasRelevantChange = !!(changes['startDate'] || changes['endDate'] || changes['startValue'] || changes['endValue'] || changes['unit']);
      const hasAllInputs = !!this.startDate && !!this.endDate &&
        this.startValue !== undefined && this.startValue !== null &&
        this.endValue !== undefined && this.endValue !== null &&
        !!this.unit;
      if (hasRelevantChange && hasAllInputs && this.polyCadenceControl && this.polyToleranceControl) {
       // console.log("Date is changing or value/unit inputs changed, attempting auto-generate")
        this.tryAutoGenerate();
      }
    }
  }

  private buildPolymorphicControls(): void {
    let initialCadence = 'Daily';
    let initialTolerance = '5';
    
    if (this.isEditing && this.plan) {
      initialCadence = this.convertSecondsToCanence(this.plan.cadence);
      initialTolerance = this.plan.tolerance?.toString() || '5';
    } else if (this.planForm) {
      initialCadence = typeof this.planForm.cadence === 'number' 
        ? this.convertSecondsToCanence(this.planForm.cadence)
        : 'Daily';
      initialTolerance = this.planForm.tolerance?.toString() || '5';
    }

    this.polyCadenceControl = this.polymorphicControlService.getC({
      id: 'cadence',
      type: PolyMorphicControlTypes.options,
      label: 'Cadence',
      icon: 'cilCalendar',
      optionsWithIcon: this.cadenceOptions.map(option => ({
        id: option,
        label: option,
        icon: ''
      })),
      value: initialCadence || 'Daily',
      optionSelected: initialCadence || 'Daily',
      optionHeader: 'Select Cadence',
      optionPlaceholder: 'Choose cadence',
      floatingLabel: true,
        fieldMeta: {
        id: 'cadence',
        name: 'Cadence',
        type: FieldTypes.text,
        label: 'Cadence Field',
        isRequired: true,
      },
      optionChangeCallback: (selectedCadence: {id: string, label: string, icon: string}) => {
        this.onCadenceChange(selectedCadence?.id);
      }
    });

    // Tolerance number input
    this.polyToleranceControl = this.polymorphicControlService.getC({
      id: 'tolerance',
      type: PolyMorphicControlTypes.number,
      label: 'Tolerance (in %)',
      icon: 'cilBalanceScale',
      value: initialTolerance,
      floatingLabel: true,
      fieldMeta: {
        id: 'tolerance',
        name: 'Tolerance',
        type: FieldTypes.number,
        label: 'Tolerance Field',
        min: 0,
        max: 100,
        isRequired: true,
      },
    });
  }

  private validateCadenceForDateRange(startDate: string, endDate: string, cadence: string): boolean {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDifference = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
   
    switch (cadence) {
      case 'Weekly': return daysDifference >= 7;
      case 'Fortnightly': return daysDifference >= 14;
      case 'Monthly': return daysDifference >= 30;
      case 'Quarterly': return daysDifference >= 90;
      case 'Half-yearly': return daysDifference >= 180;
      default: return true; 
    }
  }

  private findNearestValidCadence(startDate: string, endDate: string, requestedCadence: string): string {
    const cadenceOrder = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Half-yearly'];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDifference = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const requestedIndex = cadenceOrder.indexOf(requestedCadence);

    for (let i = requestedIndex; i >= 0; i--) {
      const cadence = cadenceOrder[i];
      switch (cadence) {
        case 'Half-yearly':
          if (daysDifference >= 180) return cadence;
          break;
        case 'Quarterly':
          if (daysDifference >= 90) return cadence;
          break;
        case 'Monthly':
          if (daysDifference >= 30) return cadence;
          break;
        case 'Fortnightly':
          if (daysDifference >= 14) return cadence;
          break;
        case 'Weekly':
          if (daysDifference >= 7) return cadence;
          break;
        case 'Daily':
          return cadence; 
      }
    }

    return 'Daily'; 
  }


  onCadenceChange(selectedCadence: string): void { 
    if (this.startDate && this.endDate && !this.validateCadenceForDateRange(this.startDate, this.endDate, selectedCadence)) {
      const nearestCadence = this.findNearestValidCadence(this.startDate, this.endDate, selectedCadence);
      this.toastService.toast(`Date range is too short for ${selectedCadence} cadence. Using ${nearestCadence} instead.`);
      this.planForm['cadence'] = this.mapCadanceIdWithOptions(nearestCadence);
      // Update polymorphic control
      this.polyCadenceControl.optionSelected = nearestCadence;
    } else {
      this.planForm['cadence'] = this.mapCadanceIdWithOptions(selectedCadence);
    }
    this.tryAutoGenerate();
  }

  onToleranceChange(): void {
    this.planForm['tolerance'] = parseFloat(this.polyToleranceControl.value) || 0;
  }

  mapCadanceIdWithOptions(cadenceId: string){
    return cadenceOptions[cadenceId as keyof typeof cadenceOptions];
  }

  private validateInputs(startValue: number, endValue: number, startDate: string, endDate: string): boolean {
    const errors: string[] = [];
    if (!startDate) errors.push('Please select start date');
    if (!endDate) errors.push('Please select end date');
    if (startDate > endDate) errors.push('Start date should be earlier than end date'); 
    if (errors.length > 0) {
      this.toastService.toast(errors.join('\n'));
      return false;
    }
    return true;
  }

  generatePlan() {
    const startValue = this.startValue;
    const endValue = this.endValue;
    const startDate = this.startDate;
    const endDate = this.endDate;
    if (!this.validateInputs(startValue, endValue, startDate, endDate)) return;
    if (!this.isFormValid) return;
    const cadance = this.polyCadenceControl?.optionSelected; 
    if (!this.validateCadenceForDateRange(startDate, endDate, cadance)) {
      const nearestCadence = this.findNearestValidCadence(startDate, endDate, cadance);
      this.toastService.toast(`Date range is too short for ${this.planForm.cadence} cadence. Using ${nearestCadence} instead.`);
      this.planForm['cadence'] = this.mapCadanceIdWithOptions(nearestCadence);
    }

    const updatedCadence = this.polyCadenceControl?.optionSelected;
    const tolerance = Number(this.polyToleranceControl?.value);
    const cadenceInSeconds = this.convertCadenceToSeconds(updatedCadence);
    const planItems = this.calculatePlanItems(startValue, endValue, startDate, endDate, cadenceInSeconds);
    
    this.generatedPlan = {
      cadence: cadenceInSeconds,
      tolerance: tolerance,
      planItems: planItems,
       deliverableId: this.plan ? this.plan.deliverableId : ''
    };
    if(this.generatedPlan)
    {
      this.listView = this.buildPlanListView(this.generatedPlan);
    }

    this.planGenerated.emit(this.generatedPlan);
  }

  onEditPlanItem(planItem: iPlanItems): void {
    this.selectedPlanItemId = planItem.id;
    const activePlan = this.getActivePlan();
    const existingDates = (activePlan?.planItems ?? [])
      .filter(item => item.id !== planItem.id)
      .map(item => this.toDateInputValue(item.date));

    launchPlanItemEditDialog(
      this.toDateInputValue(planItem.date),
      planItem.expected,
      planItem.unit?.symbol || '',
      this.optimaDefaultModalService,
      (date: string, expected: number) => this.applyPlanItemEdit(date, expected),
      {
        minDate: this.startDate || undefined,
        maxDate: this.endDate || undefined,
        minExpected: Math.min(this.startValue, this.endValue),
        maxExpected: Math.max(this.startValue, this.endValue),
        existingDates,
      }
    );
  }

  private deleteDialogLock = false;

  onDeletePlanItem(planItem: iPlanItems): void {
    const activePlan = this.getActivePlan();
    if (!activePlan) return;

    if (activePlan.planItems.length <= 1) {
      this.toastService.toast('Cannot delete — a plan must have at least one item.');
      return;
    }

    if (this.deleteDialogLock) return;
    this.deleteDialogLock = true;
    // 400ms covers the modal's 250ms close animation before allowing a new dialog
    setTimeout(() => { this.deleteDialogLock = false; }, 400);

    launchPlanItemDeleteDialog(
      'Are you sure you want to delete this plan item?',
      this.optimaDefaultModalService,
      () => {
        activePlan.planItems = activePlan.planItems.filter(item => item.id !== planItem.id);
        this.updatePlanState(activePlan);
      }
    );
  }

  onResetPlan(): void {
    launchPlanItemDeleteDialog(
      'This will discard all manual edits and regenerate the plan from the current cadence, tolerance, and date settings.',
      this.optimaDefaultModalService,
      () => this.generatePlan(),
      { title: 'Regenerate Plan', confirmLabel: 'Regenerate', confirmColor: 'primary' }
    );
  }

  onAddPlanItem(): void {
    const activePlan = this.getActivePlan();
    const existingDates = (activePlan?.planItems ?? []).map(item => this.toDateInputValue(item.date));
    const referenceUnit = activePlan?.planItems?.[activePlan.planItems.length - 1]?.unit ?? this.unit;

    launchPlanItemEditDialog(
      '',
      this.startValue,
      referenceUnit?.symbol || '',
      this.optimaDefaultModalService,
      (date: string, expected: number) => this.applyPlanItemAdd(date, expected, referenceUnit),
      {
        minDate: this.startDate || undefined,
        maxDate: this.endDate || undefined,
        minExpected: Math.min(this.startValue, this.endValue),
        maxExpected: Math.max(this.startValue, this.endValue),
        existingDates,
      }
    );
  }

  private applyPlanItemAdd(date: string, expected: number, unit: Unit | undefined): void {
    const activePlan = this.getActivePlan();
    if (!activePlan) return;

    const resolvedUnit = unit ?? (activePlan.planItems[0]?.unit);
    const newItem: iPlanItems = {
      id: crypto.randomUUID(),
      sortOrder: 0,
      date: new Date(date),
      expected,
      unitId: resolvedUnit?.id ?? '',
      isManual: true,
      unit: resolvedUnit,
    };

    activePlan.planItems = [...activePlan.planItems, newItem]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    this.updatePlanState(activePlan);
  }

  private applyPlanItemEdit(date: string, expected: number): void {
    const activePlan = this.getActivePlan();
    if (!activePlan) {
      return;
    }

    const planItem = activePlan.planItems.find(item => item.id === this.selectedPlanItemId);
    if (!planItem) {
      return;
    }

    planItem.date = new Date(date);
    planItem.expected = expected;
    this.selectedPlanItemId = '';
    activePlan.planItems = [...activePlan.planItems].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    this.updatePlanState(activePlan);
  }

  private buildPlanListView(plan: iPlan): IListView<iPlan> {
    return getPlanListView(
      plan,
      this.utilService,
      (planItem) => this.onEditPlanItem(planItem),
      (planItem) => this.onDeletePlanItem(planItem)
    );
  }

  private getActivePlan(): iPlan | undefined {
    if (this.generatedPlan && Array.isArray(this.generatedPlan.planItems)) {
      return this.generatedPlan;
    }

    if (this.plan && Array.isArray(this.plan.planItems)) {
      return this.plan;
    }

    return undefined;
  }

  private updatePlanState(plan: iPlan): void {
    const normalizedPlanItems = plan.planItems.map((item, index) => ({
      ...item,
      sortOrder: index + 1,
    }));

    const updatedPlan: iPlan = {
      ...plan,
      planItems: normalizedPlanItems,
    };

    this.generatedPlan = updatedPlan;
    this.planForm = {
      ...this.planForm,
      cadence: updatedPlan.cadence,
      tolerance: updatedPlan.tolerance,
      deliverableId: updatedPlan.deliverableId,
      planItems: updatedPlan.planItems,
    };
    this.listView = this.buildPlanListView(updatedPlan);
    this.planGenerated.emit(updatedPlan);
  }

  private toDateInputValue(date: Date): string {
    const currentDate = new Date(date);
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private convertCadenceToSeconds(cadence: string): number {
    const SECONDS_IN_DAY = 86400;
    const SECONDS_IN_WEEK = SECONDS_IN_DAY * 7;
    const SECONDS_IN_MONTH = SECONDS_IN_DAY * 30; 

    switch (cadence) {
      case 'Daily': return SECONDS_IN_DAY;
      case 'Weekly': return SECONDS_IN_WEEK;
      case 'Fortnightly': return SECONDS_IN_WEEK * 2;
      case 'Monthly': return SECONDS_IN_MONTH;
      case 'Quarterly': return SECONDS_IN_MONTH * 3;
      case 'Half-yearly': return SECONDS_IN_MONTH * 6;
      default: return SECONDS_IN_DAY;
    }
  }

    private convertSecondsToCanence(seconds: number): string {
    const SECONDS_IN_DAY = 86400;
    const SECONDS_IN_WEEK = SECONDS_IN_DAY * 7;
    const SECONDS_IN_MONTH = SECONDS_IN_DAY * 30;

    switch (seconds) {
      case SECONDS_IN_DAY: return 'Daily';
      case SECONDS_IN_WEEK: return 'Weekly';
      case SECONDS_IN_WEEK * 2: return 'Fortnightly';
      case SECONDS_IN_MONTH: return 'Monthly';
      case SECONDS_IN_MONTH * 3: return 'Quarterly';
      case SECONDS_IN_MONTH * 6: return 'Half-yearly';
      default: return 'Daily';
    }
  }

  private calculatePlanItems(start: number, end: number, startDate: string, endDate: string, cadenceInSeconds: number): iPlanItems[] {
    const items: iPlanItems[] = [];
    const startDt = new Date(startDate);
    const endDt = new Date(endDate);
    const intervals = this.getIntervals(cadenceInSeconds, startDt, endDt);

    for (let index = 0; index < intervals.length; index++) {
      const date = intervals[index];

      const progress = start + ((end - start) / (intervals.length - 1)) * (index);
      let unit = Units.find((u: Unit) => u.id === this.unit?.id) as Unit;
      let percentageunit = Units.find((u: Unit) => u.category === UnitCategory.Percentage);
      let setUnit: Unit = percentageunit as Unit; // Default to percentage if no matching unit found
      if(this.unit){
        setUnit = unit
      } 
      items.push({
        id: crypto.randomUUID(),
        sortOrder: index + 1,
        date: date,
        expected: Math.round(progress * 100) / 100,
        unitId: setUnit.id,
        isManual: false,
        unit: setUnit
      });
    }

    return items;
  }

  private getIntervals(cadenceInSeconds: number, start: Date, end: Date): Date[] {
    const dates: Date[] = [];
    let currentDate = new Date(start);

    while (currentDate <= end) {
      dates.push(new Date(currentDate));
      currentDate = new Date(currentDate.getTime() + cadenceInSeconds * 1000);
    }
    return dates;
  }

  get isPlanFormValid(): boolean {
    return this.isToleranceValid() && this.isCadenceValid() && this.isDateRangeValidForCadence() && this.isPlanRangeComplete();
  }


  private isPlanRangeComplete(): boolean {
    const activePlan = this.getActivePlan();
    if (!activePlan?.planItems?.length) return true;
    if (this.endValue === this.startValue) return true;
    const min = Math.min(this.startValue, this.endValue);
    const max = Math.max(this.startValue, this.endValue);
    return activePlan.planItems.every(item => item.expected >= min && item.expected <= max);
  }

  private isToleranceValid(): boolean {
    const tolerance = this.polyToleranceControl?.value;
    
    if (tolerance === null || tolerance === undefined || tolerance === '') {
      return false;
    }

    const toleranceNum = Number(tolerance);
    
    if (isNaN(toleranceNum)) {
      return false;
    }

    // Validate tolerance range (0-100 percent)
    if (toleranceNum < 0 || toleranceNum > 100) {
      return false;
    }

    return true;
  }

  private isCadenceValid(): boolean {
    const cadence = this.polyCadenceControl?.optionSelected;
    
    if (!cadence || cadence === '') {
      return false;
    }

    // Validate that the selected cadence is in the valid options
    if (!this.cadenceOptions.includes(cadence)) {
      return false;
    }

    return true;
  }

  private isDateRangeValidForCadence(): boolean {
  // If dates are not provided yet, consider it valid (will be validated elsewhere)
  if (!this.startDate || !this.endDate) {
    return true;
  }

  const cadence = this.polyCadenceControl?.optionSelected;
  
  if (!cadence) {
    return false;
  }

  return this.validateCadenceForDateRange(this.startDate, this.endDate, cadence);
}
  

  isGenerateButtonDisabled() {
    // Check if dates are provided and valid
    let datesInvalid = !this.startDate || !this.endDate || new Date(this.startDate) > new Date(this.endDate);
    
    // Check if values are provided (allow 0 as valid)
    let valuesInvalid = this.startValue === undefined || this.startValue === null || 
                        this.endValue === undefined || this.endValue === null;
    
    const isDisabled = !this.isFormValid || valuesInvalid || datesInvalid;
    return isDisabled;
  }

  private tryAutoGenerate(): void {
    // Check if all required inputs are available and valid
    const hasDates = !!this.startDate && !!this.endDate;
    const hasValues = this.startValue !== undefined && this.startValue !== null && 
                      this.endValue !== undefined && this.endValue !== null;
    const hasCadence = !!this.polyCadenceControl?.optionSelected;
    const toleranceValue = this.polyToleranceControl?.value;
    const hasTolerance = toleranceValue !== null && toleranceValue !== undefined && toleranceValue !== '';
    if (hasDates && hasValues && hasCadence && hasTolerance && this.isFormValid) {
      setTimeout(() => {
        this.generatePlan();
      }, 100);
    }
  }
}
